import { Pencil, Eye, Receipt as ReceiptIcon, FileWarning } from 'lucide-react';
import type { Expense } from '../../types/expense';
import type { UserRole } from '../../types/user';
import { Button } from '../ui/Button';
import { formatDate, formatTime } from '../../utils/trips';
import { colorDeCategoria, formatearCOP, puedeCorregir, dentroDeVentanaEdicion, sinRecibo } from '../../utils/expenses';

interface Props {
  expenses: Expense[];
  role?: UserRole;
  onVerDetalle: (expense: Expense) => void;
  /**
   * Opcional a propósito: una vista de solo lectura (el expediente del viaje)
   * simplemente no la pasa. Omitir el callback es lo que oculta el botón — más
   * claro que fingir un rol sin permisos para lograr el mismo efecto.
   */
  onCorregir?: (expense: Expense) => void;
}

// Última columna de ancho fijo: un `auto` se mide fila por fila (cada fila es su
// propio grid) y desalineaba las filas con más botones. 100 px = 2 botones de
// 44 px (objetivo táctil de móvil) + el hueco.
const GRID = '120px 1fr 130px 1fr 1fr 90px 100px';

/**
 * FE-12 — el listado denso de gastos.
 *
 * La columna del **recibo** es la que justifica la tabla. Con el recibo
 * opcional, «sin recibo» no es un detalle estético: es un gasto sin soporte
 * contable, y verlo de un vistazo en cuarenta filas es lo que permite ir a
 * completarlos antes de cerrar el mes.
 *
 * El importe se alinea a la derecha, como toda columna de dinero: comparar
 * magnitudes de un vistazo depende de que las unidades queden en la misma
 * vertical.
 */
export function ExpensesTable({ expenses, role, onVerDetalle, onCorregir }: Props) {
  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: '860px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: '0.75rem', padding: '0.75rem 1rem', background: 'var(--bg-raised)', borderBottom: '1px solid var(--border)', fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
            <span>Fecha</span>
            <span>Categoría</span>
            <span style={{ textAlign: 'right' }}>Importe</span>
            <span>Vehículo / conductor</span>
            <span>Nota</span>
            <span>Recibo</span>
            <span style={{ textAlign: 'right' }}>Acciones</span>
          </div>

          {expenses.map(e => (
            <div
              key={e.id}
              id={`expense-row-${e.id}`}
              style={{ display: 'grid', gridTemplateColumns: GRID, gap: '0.75rem', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', alignItems: 'center', fontSize: '12px', color: 'var(--text-secondary)' }}
            >
              <span style={{ display: 'flex', flexDirection: 'column' }}>
                <strong style={{ color: 'var(--text-primary)' }}>{formatDate(e.incurredAt)}</strong>
                <span style={{ color: 'var(--text-muted)' }}>{formatTime(e.incurredAt)}</span>
              </span>

              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: colorDeCategoria(e.categoryCode), flexShrink: 0 }} />
                <span style={{ color: 'var(--text-primary)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {e.categoryLabel}
                </span>
              </span>

              <span style={{ textAlign: 'right', fontWeight: 800, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                {formatearCOP(e.amount)}
              </span>

              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {e.vehiclePlate || (e.vehicleId ? 'vehículo' : '—')}
                </span>
                <span style={{ color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {e.driverName ?? (e.driverId ? 'conductor' : '—')}
                </span>
              </span>

              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.notes}>
                {e.notes || <em style={{ color: 'var(--text-muted)' }}>—</em>}
              </span>

              <span>
                {sinRecibo(e) ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '11px', fontWeight: 700, color: '#F59E0B' }}>
                    <FileWarning size={13} /> Falta
                  </span>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '11px', fontWeight: 700, color: '#10B981' }}>
                    <ReceiptIcon size={13} /> Sí
                  </span>
                )}
              </span>

              {/* La clase existe para el objetivo táctil de móvil (index.css):
                  a 386 px los `btn--sm` salen a 26 px y el dedo no es un
                  puntero (`Frontend_Rulebook.md` §4). */}
              <span className="expenses-table__actions" style={{ display: 'flex', gap: '0.375rem', justifyContent: 'flex-end', alignItems: 'center' }}>
                {onCorregir && puedeCorregir(role) && (
                  dentroDeVentanaEdicion(e, role) ? (
                    <Button size="sm" variant="secondary" onClick={() => onCorregir(e)} aria-label="Corregir gasto"><Pencil size={13} /></Button>
                  ) : (
                    <Button
                      size="sm" variant="secondary" disabled
                      title="Pasaron más de 2 semanas desde el registro: ya no se puede corregir."
                      aria-label="Corregir gasto (ventana de edición vencida)"
                    >
                      <Pencil size={13} />
                    </Button>
                  )
                )}
                <Button size="sm" variant="ghost" onClick={() => onVerDetalle(e)} aria-label="Ver detalle del gasto"><Eye size={13} /></Button>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
